import { useState } from "react";
import { format, parseISO } from "date-fns";
import { Box, IconButton, Tooltip } from "@mui/material";
import { Edit, EventRepeat, InsertInvitation } from "@mui/icons-material";
import Swal from "sweetalert2";

import { CustomToolbarButtonComponent } from "@shared/ui/MuiTable";
import { DataGridTable } from "@shared/ui/DataGridTable";
import { SuccessAlert, ErrorAlert } from "@shared/ui/alerts";
import { useChargeHours } from "../hooks/useChargeHours";
import { getProjectIssues } from "../api/timeEntryApi";
import { SPECIAL_ACTIVITY_IDS } from "../constants";
import { buildWeekChargeEntry } from "../lib/timeEntryMappers";
import { MonthlyHourDialog } from "./MonthlyHourDialog";

import type { GridColDef, GridRenderCellParams } from "@mui/x-data-grid";
import type { MissingHoursWeeklySummary, MonthlySummaryHours } from "../api/timeEntryApi";
import type { TFunction } from "i18next";

export interface MissingHourSummaryProps {
  notLoading: () => void;
  thisLoading: () => void;
  openDialog: (missingHour: MissingHoursWeeklySummary) => void;
  data?: MissingHoursWeeklySummary[];
  t: TFunction;
  language?: string;
  projectData?: MonthlySummaryHours[];
  reloadProjectData: () => void;
  reloadInitialMissingHour: () => void;
}

export const MissingHourSummary = ({
  notLoading,
  thisLoading,
  openDialog,
  data,
  t,
  language,
  projectData,
  reloadProjectData,
  reloadInitialMissingHour,
}: MissingHourSummaryProps) => {
  const [showProjects, setShowProjects] = useState(false);
  const [openMonthlyDialog, setMonthlyDialog] = useState(false);
  const [monthlyHour, setMonthlyHour] = useState<MonthlySummaryHours>();

  const onClickOpenDialog = (missingHour: MissingHoursWeeklySummary) => {
    openDialog(missingHour);
  };

  const onEdit = (monthlyHour: MonthlySummaryHours) => {
    setMonthlyHour(monthlyHour);
    onOpenMonthlyDialog();
  };

  const chargeHours = useChargeHours();

  // Carga masiva de horas de la semana para una fila de horas faltantes.
  // Llama la API directamente (flujo imperativo con Swal de confirmación).
  const onchargedWeekHour = async (missingHour: MissingHoursWeeklySummary) => {
    // `missingHour.projectId` puede ser undefined en el contrato; comportamiento
    // preexistente sin guard (ver #2572).
    const issueResponse = await getProjectIssues(missingHour.projectId!);
    // Comportamiento preexistente: se asume que el proyecto tiene al menos una issue.
    const issueId = issueResponse[0]!.id;

    const template = `<p>${t("textChargeWeekHour", {
      hours: missingHour.missingHours,
      project: missingHour.projectName,
    })}</p><br/>
      <ul>${missingHour.days!
        .map((item) => `<li>${format(parseISO(item.date!), "dd/MM/yyyy")}</li>`)
        .join("")}</ul>`;

    Swal.fire({
      title: t("loadHours"),
      html: template,
      icon: "question",
      showCancelButton: true,
      confirmButtonText: t("load"),
      cancelButtonText: t("cancel"),
    }).then(({ isConfirmed }) => {
      if (isConfirmed) {
        // `buildWeekChargeEntry` espera `projectId`/`missingHours`/`from` no opcionales
        // (asume que el call site siempre los pobló); el contrato generado los declara
        // opcionales, de ahí el `as never`. `issueId` ídem `issueId!` más arriba.
        // Este call site (carga masiva desde la grilla, sin form/yup de por medio) envía
        // `projectId` como number crudo del DTO, a diferencia de los dialogs con Select que
        // lo castean a string vía yup — `ChargeHoursPayload.projectId` se tipó pensando en
        // esos otros call sites (slice 1); se documenta el cast en vez de forzar un
        // String(...) que cambiaría el payload real enviado al backend.
        chargeHours.mutate(buildWeekChargeEntry(missingHour as never, issueId!) as never, {
          onSuccess: () => {
            notLoading();
            SuccessAlert(t("loadHours"), t("doneSuccessfully", { a: "a" })).then(
              () => reloadInitialMissingHour()
            );
          },
          onError: (error) => {
            const data = error.response?.data;
            notLoading();
            ErrorAlert(t("errorTitle"), data?.message ?? t("internalError")).then();
          },
        });
      }
    });
  };

  const onOpenMonthlyDialog = () => setMonthlyDialog(true);
  const onCloseMonthlyDialog = () => setMonthlyDialog(false);

  const columns: GridColDef[] = [
    { field: "projectName", headerName: t("project"), flex: 1 },
    {
      field: "from",
      headerName: t("from"),
      flex: 1,
      valueFormatter: (value: string | null) =>
        value == null ? "" : format(parseISO(value), "dd/MM/yyyy"),
    },
    {
      field: "to",
      headerName: t("to"),
      flex: 1,
      valueFormatter: (value: string | null) =>
        value == null ? "" : format(parseISO(value), "dd/MM/yyyy"),
    },
    {
      field: "missingHours",
      headerName: t("missingHours"),
      flex: 1,
      // Bug preexistente: `MissingHourDailySummaryDTO` (contrato generado y DTO de Java) no
      // declara `activityId` en `days[]`, pero este código lo lee para detectar actividades
      // especiales, así que en la práctica no detecta ninguna. `params.row` se castea a una
      // forma local con `activityId` opcional (sin tocar el tipo del contrato) y `?? -1`
      // cubre el `undefined` sin cambiar el resultado: -1 no está en SPECIAL_ACTIVITY_IDS.
      renderCell: (params: GridRenderCellParams) => {
        const days = (params.row as { days?: { activityId?: number }[] }).days;
        const hasSpecialActivity = days?.some((day) =>
          SPECIAL_ACTIVITY_IDS.includes(day.activityId ?? -1)
        );
        return hasSpecialActivity ? "0" : params.value;
      },
    },
    {
      field: "registerHour",
      headerName: t("registerHour"),
      sortable: false,
      filterable: false,
      minWidth: 120,
      renderCell: (params: GridRenderCellParams) => (
        <>
          <IconButton onClick={() => onClickOpenDialog(params.row)}>
            <InsertInvitation />
          </IconButton>
          <Tooltip title={t("weeklyHoursLoad")}>
            <IconButton onClick={() => onchargedWeekHour(params.row)}>
              <EventRepeat sx={{ mt: "-1px" }} />
            </IconButton>
          </Tooltip>
        </>
      ),
    },
  ];

  const projectColumns: GridColDef[] = [
    { field: "projectName", headerName: t("project"), flex: 1 },
    {
      field: "spentOn",
      headerName: t("date"),
      flex: 1,
      valueFormatter: (value: string | null) =>
        value == null ? "" : format(parseISO(value), "dd/MM/yyyy"),
    },
    { field: "hours", headerName: t("hours"), flex: 1 },
    { field: "activityName", headerName: t("activities"), flex: 1 },
    { field: "comment", headerName: t("description"), flex: 1 },
    {
      field: "Acciones",
      headerName: t("actions"),
      sortable: false,
      filterable: false,
      renderCell: (params: GridRenderCellParams) => (
        <Box sx={{ display: "flex", flexDirection: "row" }}>
          <IconButton onClick={() => onEdit(params.row)}>
            <Edit />
          </IconButton>
        </Box>
      ),
    },
  ];

  const onClickAction = () => setShowProjects(!showProjects);

  const toolbarActions = (
    <CustomToolbarButtonComponent
      title={showProjects ? t("missingHours") : t("timesList")}
      onClickAction={onClickAction}
    />
  );

  return (
    <>
      {!showProjects && (
        <DataGridTable
          rows={data}
          title={t("missingHours")}
          columns={columns}
          language={language}
          search={false}
          download={false}
          toolbarActions={toolbarActions}
        />
      )}
      {showProjects && (
        <DataGridTable
          rows={projectData}
          title={t("timesList")}
          columns={projectColumns}
          language={language}
          search={false}
          download={false}
          toolbarActions={toolbarActions}
        />
      )}

      <MonthlyHourDialog
        handleClose={onCloseMonthlyDialog}
        open={openMonthlyDialog}
        loading={thisLoading}
        notLoading={notLoading}
        data={monthlyHour}
        t={t}
        reloadProjectData={reloadProjectData}
      />
    </>
  );
};
